import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SystemHeader from '../SystemHeader';
import NutZalo, { NutGoi } from '../../utils/NutZalo';
import { KhachLeAPI } from '../../services/feature-api';
import API from '../../services/API';
import { FeedbackForm } from '../feedback/feedback-page';
import GuideButton from '../shared/GuideButton';
import DateSelect from '../DateSelect';

// QL Khách lẻ (STT 8) — /khach-le. Nguồn: view khach_le_yody (khách role 15 × đơn lẻ type=1 của ĐẠI LÝ đang đăng nhập).
// Khách sỉ (đại lý, đơn type=2) KHÔNG có ở đây — xem /don-hang-ban-buon.
// Tình trạng: đại lý chọn tay (khach_le_cham_soc.tinh_trang); chưa chọn thì view gợi ý theo số ngày kể từ lần mua gần nhất.
// Danh mục tình trạng + gợi ý việc lấy từ DB (/khach-le/danh-muc); TINH_TRANG dưới đây chỉ để hiện tạm trước khi tải xong.
// Bản 3 (anh Bình 27/10/2026): tỉnh thành + địa chỉ (users.city/address) · gom nhóm theo tình trạng / tỉnh (thu – mở như Airtable)
// · bấm ô trên grid sửa ngay (tình trạng, tỉnh, ghi chú) · nút "Sửa" mở tấm chi tiết bằng URL /khach-le?khach=<id> (gửi link được)
// · tấm chi tiết: Việc tiếp theo lên trước Việc đã làm, bỏ ⑥ Hình ảnh ghi chú (đã gộp vào hồ sơ Kết quả khách, mốc Trong).

export const TINH_TRANG = {
  cho_nhan_hang:  { ten: 'Chờ nhận hàng',  icon: 'local_shipping',       mau: '#0EA5E9' },
  dang_dung:      { ten: 'Đang dùng',      icon: 'check_circle',         mau: '#16A34A' },
  can_cham_soc:   { ten: 'Cần chăm sóc',   icon: 'notifications_active', mau: '#D97706' },
  co_nhu_cau_lai: { ten: 'Có nhu cầu lại', icon: 'autorenew',            mau: '#2563EB' },
  tam_ngung:      { ten: 'Tạm ngưng',      icon: 'pause_circle',         mau: '#6B7280' },
  khac:           { ten: 'Khác',           icon: 'more_horiz',           mau: '#7C3AED' },
};

const Icon = ({ name, size = 16, className = '', style }) => (
  <span className={`material-symbols-rounded ${className}`} style={{ fontSize: size, lineHeight: 1, ...style }}>{name}</span>
);
const fmtTien = (n) => `${Math.round(Number(n) || 0).toLocaleString('vi-VN')}`;
const fmtNgay = (iso) => (iso ? `${String(iso).slice(8, 10)}/${String(iso).slice(5, 7)}/${String(iso).slice(2, 4)}` : '—');
// Bảo mật: chỉ hiện 3 số cuối SĐT khách (anh Bình chốt 23/09/2026); link Zalo vẫn dùng SĐT đầy đủ.
const sdtNgan = (sdt) => (sdt ? `***${String(sdt).slice(-3)}` : '—');
// Tối đa 2 dòng, dài hơn thì … (anh Bình chốt 23/09/2026)
const HAI_DONG = { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' };
const homNayISO = () => new Date().toLocaleDateString('sv-SE');
const cachDay = (d) => (d === 0 ? 'Hôm nay' : d === 1 ? 'Hôm qua' : `${d} ngày`);
// users.city có cả "Bình Dương" lẫn "Tỉnh Bình Dương" (nhập từ nhiều nguồn) → bỏ tiền tố để hiện + gom nhóm chung
const rutTinh = (t) => String(t || '').replace(/^(tỉnh|thành phố|tp\.?)\s+/i, '').trim();
const CHUA_TINH = 'Chưa có tỉnh';

const ChipTinhTrang = ({ ma, size = 'sm', onClick, ds = TINH_TRANG, noi }) => {
  const t = ds[ma] || ds.khac || TINH_TRANG.khac;
  return (
    <button type="button" onClick={onClick}
            className={`inline-flex items-center gap-1 rounded-xl font-bold text-left leading-tight ${size === 'lg' ? 'text-[12px] px-3 py-1.5 whitespace-nowrap' : 'text-[10.5px] px-1.5 py-0.5'}`}
            style={{ backgroundColor: noi ? t.mau : `${t.mau}18`, color: noi ? '#fff' : t.mau, border: `1px solid ${t.mau}${noi ? '' : '55'}` }}>
      <Icon name={t.icon} size={size === 'lg' ? 16 : 13} />{t.ten}
    </button>
  );
};

// Zalo trợ lý Yody = tài khoản gửi tin nhắc việc 7:30 (PE_Core nhac-viec.service). "Đã kết nối" = đại lý đã là bạn bè Zalo với trợ lý
// (la_ban, lấy qua find-user theo SĐT đăng nhập). Chưa là bạn → nút mở zalo.me/<SĐT trợ lý> để gửi lời mời; trợ lý đồng ý → quay lại app tự kiểm.
const useZaloTroLy = (accessToken) => {
  const [z, setZ] = useState(null); // null = đang tải
  const [dang, setDang] = useState(false);
  const daBam = useRef(false);
  const kiem = useCallback(async () => {
    setDang(true);
    const r = await KhachLeAPI.ketNoiZalo(accessToken);
    setDang(false);
    setZ((cu) => ({ ...cu, ...(r.success && r.data?.ok ? r.data : { loi: r.data?.error || r.error || 'Chưa kiểm được' }) }));
  }, [accessToken]);
  useEffect(() => {
    if (!accessToken) return;
    KhachLeAPI.getZalo(accessToken).then((r) => {
      const d = r.success && r.data ? r.data : {};
      setZ(d);
      if (!d.kiem_luc) kiem(); // chưa kiểm lần nào → tự kiểm 1 lần (đại lý có thể đã là bạn bè từ trước)
    });
  }, [accessToken, kiem]);
  useEffect(() => {
    const quayLai = () => { if (document.visibilityState === 'visible' && daBam.current) { daBam.current = false; kiem(); } };
    document.addEventListener('visibilitychange', quayLai);
    return () => document.removeEventListener('visibilitychange', quayLai);
  }, [kiem]);
  return { z, dang, kiem, bam: () => { daBam.current = true; } };
};

const ZaloTroLy = ({ zt, gon }) => {
  const { z, dang, kiem, bam } = zt;
  if (!z) return null;
  if (z.la_ban) {
    return (
      <div className={`flex items-center gap-1.5 rounded-xl bg-green-50 border border-green-200 text-green-800 ${gon ? 'px-2 py-1.5 text-[11.5px]' : 'px-3 py-2 text-[12.5px]'}`}>
        <span>📲</span><span className="flex-1"><b>Zalo trợ lý Yody: Đã kết nối ✓</b>{gon ? '' : ' — 7:30 sáng ngày hẹn sẽ nhắn bạn việc cần làm'}</span>
      </div>
    );
  }
  return (
    <div className={`rounded-xl bg-amber-50 border border-amber-300 text-amber-900 ${gon ? 'px-2 py-1.5 text-[11.5px]' : 'px-3 py-2.5 text-[12.5px]'}`}>
      <p><b>📲 Chưa kết nối Zalo trợ lý Yody</b>{gon ? '' : ' — kết bạn để được nhắn nhắc việc 7:30 sáng ngày hẹn'}</p>
      <div className="flex items-center gap-2 mt-1.5">
        <a href={`https://zalo.me/${z.troLySdt}`} target="_blank" rel="noopener noreferrer" onClick={bam}
           className="px-3 py-1.5 rounded-lg bg-[#0068FF] text-white font-bold whitespace-nowrap">Kết bạn Zalo trợ lý</a>
        <button type="button" onClick={kiem} disabled={dang} className="px-2.5 py-1.5 rounded-lg bg-white border border-amber-300 font-bold disabled:opacity-50">
          {dang ? 'Đang kiểm…' : 'Kiểm lại'}</button>
      </div>
      {!gon && <p className="mt-1 text-[11px] text-amber-800">Bấm Kết bạn → trong Zalo bấm "Kết bạn" → trợ lý đồng ý → quay lại đây là tự hiện Đã kết nối.</p>}
      {z.loi && <p className="mt-1 text-[11px] text-red-600">{z.loi}</p>}
    </div>
  );
};

// Thanh Zalo cũ tốn 2–3 dòng đầu trang → thu thành 1 chip nhỏ cạnh số liệu; bấm mới mở tấm hướng dẫn kết bạn (anh Bình 27/10)
const ChipZalo = ({ zt, onMo }) => {
  const { z } = zt;
  if (!z) return null;
  return z.la_ban
    ? <span className="shrink-0 inline-flex items-center gap-0.5 text-[10.5px] font-bold px-1.5 py-0.5 rounded-full bg-green-50 border border-green-200 text-green-800">📲 Zalo ✓</span>
    : <button type="button" onClick={onMo} className="shrink-0 inline-flex items-center gap-0.5 text-[10.5px] font-bold px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-300 text-amber-900">📲 Kết bạn Zalo</button>;
};

const taoDonCho = (navigate, khachId) => navigate(`/don-hang/tao-moi?ve=/khach-le${khachId ? `&khach=${khachId}` : ''}`);

// sua: ô bấm trên grid mở tấm "sửa nhanh" ngay (không phải vào form lớn) — anh Bình 27/10/2026
const COT = [
  { key: 'facebook',          ten: 'Khách',        rong: 104, dinh: true },
  { key: 'tinh_trang',        ten: 'Tình trạng',   rong: 78,  sua: 'tinh_trang' }, // chữ xuống 2 hàng, đỡ tốn chỗ (anh Bình 20/10)
  { key: 'tinh',              ten: 'Tỉnh',         rong: 56,  sua: 'tinh' }, // hẹp nhất có thể, tên tỉnh ngắt 2 dòng (anh Bình 27/10)
  { key: 'so_feedback',       ten: 'Hồ sơ kết quả', rong: 58, so: true }, // số hồ sơ 23.1 của khách; bấm = bắt đầu / mở tiếp hành trình ảnh
  { key: 'so_lan_mua',        ten: 'Số lần',       rong: 46,  so: true },
  { key: 'tong_tien',         ten: 'Tổng tiền',    rong: 78,  so: true },
  { key: 'mua_cach_day_ngay', ten: 'Mua cách đây', rong: 74,  so: true },
  { key: 'san_pham',          ten: 'Sản phẩm',     rong: 140 },
  { key: 'viec_tiep_han',     ten: 'Việc tiếp theo', rong: 124 },
  { key: 'ghi_chu',           ten: 'Ghi chú',      rong: 130, sua: 'ghi_chu' },
  { key: 'thao_tac',          ten: '',             rong: 54 },
];
const TD = 'px-1.5 py-2 border-b border-[#cbd5e1] border-r border-[#e2e8f0]';

const KhachLePage = ({ accessToken, userInfo, onLogout, totalPoints, onRefreshPoints }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [locTT, setLocTT] = useState('');
  const [sort, setSort] = useState({ key: 'mua_cach_day_ngay', dir: 1 });
  const [nhom, setNhom] = useState(''); // '' | 'tinh_trang' | 'tinh' — gom nhóm kiểu Airtable
  const [dong, setDong] = useState(() => new Set()); // nhóm đang thu gọn
  const [nhanh, setNhanh] = useState(null); // { r, cot } — tấm sửa nhanh từ grid
  const [moZalo, setMoZalo] = useState(false);
  const [taoFb, setTaoFb] = useState(null); // khách đang mở form 23.1 Kết quả khách (nút ở cột Kết quả)
  const [dm, setDm] = useState({ tinhTrang: [], goiY: [] }); // danh mục từ DB
  const [cities, setCities] = useState([]); // bảng city — cùng danh sách với /cap-nhat-thong-tin
  const tt = useMemo(() => (dm.tinhTrang.length ? Object.fromEntries(dm.tinhTrang.map((t) => [t.ma, t])) : TINH_TRANG), [dm]);
  useEffect(() => { if (accessToken) KhachLeAPI.danhMuc(accessToken).then((r) => r.success && r.data && setDm(r.data)); }, [accessToken]);
  useEffect(() => { API.getCities().then((l) => setCities(Array.isArray(l) ? l : [])).catch(() => setCities([])); }, []);
  const zt = useZaloTroLy(accessToken);

  const load = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true); setError('');
    const r = await KhachLeAPI.getList(accessToken);
    if (r.success) setRows(Array.isArray(r.data) ? r.data : []); else setError(r.error || 'Lỗi tải dữ liệu');
    setLoading(false);
  }, [accessToken]);
  useEffect(() => { load(); }, [load]);

  // Tấm chi tiết mở theo URL /khach-le?khach=<id> (nút Sửa trên grid, nút "Khách" ở Báo cáo ngày, link anh Bình gửi) → đóng = về /khach-le
  const khachId = Number(new URLSearchParams(location.search).get('khach')) || 0;
  const chon = useMemo(() => (khachId ? rows.find((x) => Number(x.id) === khachId) || null : null), [rows, khachId]);
  const moChiTiet = (r) => navigate(`/khach-le?khach=${r.id}`);
  const dongChiTiet = () => navigate('/khach-le', { replace: true });

  const thayDong = (moi) => setRows((prev) => prev.map((r) => (r.id === moi.id ? { ...r, ...moi } : r)));

  const demTT = useMemo(() => rows.reduce((a, r) => { a[r.tinh_trang] = (a[r.tinh_trang] || 0) + 1; return a; }, {}), [rows]);
  const tongTien = useMemo(() => rows.reduce((a, r) => a + Number(r.tong_tien || 0), 0), [rows]);
  const soDon = useMemo(() => rows.reduce((a, r) => a + Number(r.so_lan_mua || 0), 0), [rows]);

  const hienThi = useMemo(() => {
    const k = q.trim().toLowerCase();
    let list = rows.filter((r) =>
      (!locTT || r.tinh_trang === locTT) &&
      (!k || [r.facebook, r.sdt, r.san_pham, r.ghi_chu, r.name, r.tinh, r.dia_chi].some((v) => String(v || '').toLowerCase().includes(k))));
    const cot = COT.find((c) => c.key === sort.key);
    list = [...list].sort((a, b) => {
      const va = a[sort.key], vb = b[sort.key];
      if (cot?.so) return (Number(va) - Number(vb)) * sort.dir;
      if (!va !== !vb) return va ? -1 : 1; // ô trống luôn xuống cuối
      return String(va || '').localeCompare(String(vb || ''), 'vi') * sort.dir;
    });
    return list;
  }, [rows, q, locTT, sort]);

  // Gom nhóm: [{ khoa, nhan, mau?, ds }] — tình trạng theo thứ tự danh mục; tỉnh theo số khách nhiều trước, "Chưa có tỉnh" cuối
  const cacNhom = useMemo(() => {
    if (!nhom) return [{ khoa: '', ds: hienThi }];
    const m = new Map();
    hienThi.forEach((r) => { const k = nhom === 'tinh' ? (rutTinh(r.tinh) || CHUA_TINH) : (tt[r.tinh_trang] ? r.tinh_trang : 'khac'); if (!m.has(k)) m.set(k, []); m.get(k).push(r); });
    const ds = [...m.entries()].map(([khoa, list]) => ({ khoa, ds: list, nhan: nhom === 'tinh' ? khoa : (tt[khoa]?.ten || khoa), mau: nhom === 'tinh' ? '#1E5A35' : tt[khoa]?.mau }));
    if (nhom === 'tinh') return ds.sort((a, b) => (a.khoa === CHUA_TINH) - (b.khoa === CHUA_TINH) || b.ds.length - a.ds.length || a.khoa.localeCompare(b.khoa, 'vi'));
    const thuTu = Object.keys(tt);
    return ds.sort((a, b) => thuTu.indexOf(a.khoa) - thuTu.indexOf(b.khoa));
  }, [hienThi, nhom, tt]);
  const batNhom = (khoa) => setDong((s) => { const n = new Set(s); if (n.has(khoa)) n.delete(khoa); else n.add(khoa); return n; });

  const bamCot = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: ['facebook', 'tinh_trang', 'san_pham', 'tinh'].includes(key) ? 1 : -1 }));

  // Lưu từ tấm sửa nhanh / tấm chi tiết: BE trả dòng view mới → thay vào bảng
  const luuNhanh = async (r, data) => {
    const kq = await KhachLeAPI.capNhat(accessToken, r.id, data);
    if (kq.success && kq.data) thayDong(kq.data);
    return kq;
  };

  const veDong = (r, i) => (
    <tr key={r.id} className={`align-middle ${i % 2 ? 'bg-[#f8fafc]' : 'bg-white'}`}>
      <td className={`sticky left-0 z-[1] ${TD} border-r-[#cbd5e1] ${i % 2 ? 'bg-[#f8fafc]' : 'bg-white'}`}>
        <button type="button" onClick={() => moChiTiet(r)} className="text-left w-full">
          <span className="block font-bold text-gray-900 leading-tight" style={{ maxWidth: 90, ...HAI_DONG }}>{r.facebook || r.name || '—'}</span>
        </button>
        <span className="flex items-center gap-1 mt-0.5">
          <span className="text-[10px] text-gray-400">{sdtNgan(r.sdt)}</span>
          <NutGoi sdt={r.sdt} nhan="" />
          <NutZalo sdt={r.sdt} nhan="" />
        </span>
      </td>
      <td className={TD}><ChipTinhTrang ma={r.tinh_trang} ds={tt} onClick={() => setNhanh({ r, cot: 'tinh_trang' })} /></td>
      <td className={`${TD} cursor-pointer`} onClick={() => setNhanh({ r, cot: 'tinh' })}>
        {r.tinh ? <span className="block text-[11px] font-semibold text-gray-900 leading-tight break-words" style={{ ...HAI_DONG, WebkitLineClamp: 3 }}>{rutTinh(r.tinh)}</span> : <span className="text-gray-300 text-[11px]">+</span>}
        {/* địa chỉ chi tiết KHÔNG hiện chữ trên grid (nowrap làm cột nới rộng) — chỉ 1 dấu báo đã điền, rê chuột / vào tấm chi tiết mới thấy (anh Bình 04/11) */}
        {r.dia_chi && <span className="block text-[10px] text-gray-400 leading-tight" title={r.dia_chi}>📍</span>}
      </td>
      <td className={`${TD} text-right`}>
        <button type="button" onClick={() => (r.ket_qua_id ? navigate(`/feedback-ca-nhan/${r.ket_qua_id}`, { state: { ve: '/khach-le' } }) : setTaoFb(r))}
                title={r.ket_qua_id ? 'Mở hồ sơ Kết quả của khách (bổ sung ảnh Trước / Trong / Sau)' : 'Lập hồ sơ Kết quả khách'}
                className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-bold ${r.so_feedback ? 'bg-fuchsia-100 text-fuchsia-800' : 'bg-gray-100 text-gray-400'}`}>
          <Icon name="add_a_photo" size={13} />{r.so_feedback || 0}
        </button>
      </td>
      <td className={`${TD} text-right font-semibold`}>{r.so_lan_mua}</td>
      <td className={`${TD} text-right font-semibold text-gray-900`}>{fmtTien(r.tong_tien)}</td>
      <td className={`${TD} text-right`}>
        <span className={`font-bold ${r.mua_cach_day_ngay > 60 ? 'text-red-600' : r.mua_cach_day_ngay > 30 ? 'text-amber-600' : 'text-green-700'}`}>{cachDay(r.mua_cach_day_ngay)}</span>
        <span className="block text-[10px] text-gray-400">{fmtNgay(r.lan_mua_gan_nhat)}</span>
      </td>
      <td className={`${TD} text-gray-700 text-[11px] leading-snug`} title={r.san_pham}>
        <span style={HAI_DONG}>{r.san_pham || '—'}</span>
      </td>
      <td className={`${TD} cursor-pointer`} onClick={() => moChiTiet(r)}>
        {r.viec_tiep ? (
          <>
            <span className="block font-bold text-[11.5px] text-gray-900 leading-snug" style={HAI_DONG}>{r.viec_tiep}</span>
            {r.viec_tiep_han && (
              <span className={`text-[10px] ${String(r.viec_tiep_han).slice(0, 10) < homNayISO() ? 'text-red-600 font-bold' : 'text-gray-500'}`}>
                ⏰ {fmtNgay(r.viec_tiep_han).slice(0, 5)}{r.so_viec_mo > 1 ? ` · +${r.so_viec_mo - 1} việc` : ''}
              </span>
            )}
          </>
        ) : <span className="text-gray-300 text-[11px]">+ việc cần làm</span>}
      </td>
      {/* Ghi chú in đậm như Việc tiếp theo (anh Bình 27/10); bấm = sửa ngay */}
      <td className={`${TD} cursor-pointer`} onClick={() => setNhanh({ r, cot: 'ghi_chu' })}>
        <span className="block font-bold text-[11.5px] text-gray-900 leading-snug" style={HAI_DONG}>{r.ghi_chu || <span className="text-gray-300 font-normal">+ ghi chú</span>}</span>
      </td>
      <td className="px-1 py-1 border-b border-[#cbd5e1] text-center space-y-1">
        <button type="button" onClick={() => moChiTiet(r)} className="w-12 inline-flex items-center justify-center gap-0.5 rounded-lg bg-fuchsia-50 border border-fuchsia-200 text-fuchsia-800 text-[10.5px] font-bold py-1"><Icon name="edit" size={12} />Sửa</button>
        <button type="button" onClick={() => taoDonCho(navigate, r.id)} className="w-12 rounded-lg bg-[#1E5A35] text-white text-[10.5px] font-bold py-1">Tạo đơn</button>
      </td>
    </tr>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pb-8">
      <SystemHeader userInfo={userInfo} onLogout={onLogout} totalPoints={totalPoints} pageTitle="QL Khách lẻ" onRefreshPoints={onRefreshPoints} />
      <div className="flex items-center gap-2 px-3 py-2 bg-white border-b border-gray-200 sticky top-0 z-10">
        <button type="button" onClick={() => navigate('/dashboard')}
                className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium text-fuchsia-800 bg-fuchsia-50">
          <Icon name="arrow_back" size={14} /><span>Quay lại</span>
        </button>
        <h1 className="text-sm font-bold text-gray-900 flex-1 truncate">8. QL Khách lẻ</h1>
        <button type="button" onClick={() => navigate('/feedback-ca-nhan')}
                className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-fuchsia-800 bg-fuchsia-50">23.1 Kết quả khách</button>
        <GuideButton slug="huong-dan-khach-le" />
      </div>

      {/* Tổng quan: 1 dòng số liệu (khách · đơn · tổng tiền · chip Zalo) + chip tình trạng xếp 2 hàng, không phải kéo ngang */}
      <div className="px-3 pt-3">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[22px] font-black text-gray-900 leading-none">{rows.length}</span>
            <span className="flex-1 min-w-0 text-xs text-gray-500 leading-tight">khách · <b className="text-gray-800">{soDon}</b> đơn · <b className="text-gray-800">{fmtTien(tongTien)} đ</b><br /><ChipZalo zt={zt} onMo={() => setMoZalo(true)} /></span>
            <button type="button" onClick={() => taoDonCho(navigate)}
                    className="shrink-0 flex items-center gap-0.5 px-2.5 py-1.5 rounded-lg text-[12px] font-bold text-white bg-[#1E5A35] whitespace-nowrap">
              <Icon name="add" size={15} />Đơn mới
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button type="button" onClick={() => setLocTT('')}
                    className={`text-[10.5px] font-bold px-2 py-0.5 rounded-full border ${!locTT ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-300'}`}>
              Tất cả {rows.length}
            </button>
            {Object.keys(tt).map((k) => (
              <span key={k} className={`inline-flex items-center ${locTT === k ? 'ring-2 ring-offset-1 rounded-full' : ''}`} style={{ '--tw-ring-color': tt[k].mau }}>
                <ChipTinhTrang ma={k} ds={tt} onClick={() => setLocTT(locTT === k ? '' : k)} />
                <span className="text-[10px] font-bold text-gray-500 ml-0.5">{demTT[k] || 0}</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Tìm + sắp xếp + gom nhóm */}
      <div className="px-3 pt-2 flex gap-1.5">
        <div className="flex-1 min-w-0 flex items-center gap-1.5 bg-white border border-gray-200 rounded-xl px-2.5">
          <Icon name="search" size={18} className="text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tìm tên, SĐT, SP, tỉnh, ghi chú…"
                 className="flex-1 min-w-0 py-2 text-[13px] outline-none bg-transparent" />
          {q && <button type="button" onClick={() => setQ('')}><Icon name="close" size={16} className="text-gray-400" /></button>}
        </div>
        <select value={`${sort.key}:${sort.dir}`} onChange={(e) => { const [key, dir] = e.target.value.split(':'); setSort({ key, dir: Number(dir) }); }}
                className="w-[92px] text-[11.5px] bg-white border border-gray-200 rounded-xl px-1 font-medium text-gray-700">
          <option value="mua_cach_day_ngay:1">Mua gần nhất</option>
          <option value="mua_cach_day_ngay:-1">Lâu chưa mua</option>
          <option value="viec_tiep_han:1">Việc sắp tới hạn</option>
          <option value="tong_tien:-1">Tổng tiền cao</option>
          <option value="so_lan_mua:-1">Mua nhiều lần</option>
          <option value="facebook:1">Tên A→Z</option>
        </select>
        <select value={nhom} onChange={(e) => { setNhom(e.target.value); setDong(new Set()); }}
                className={`w-[86px] text-[11.5px] border rounded-xl px-1 font-bold ${nhom ? 'bg-[#1E5A35] text-white border-[#1E5A35]' : 'bg-white text-gray-700 border-gray-200'}`}>
          <option value="">Không gom</option>
          <option value="tinh_trang">Gom: Tình trạng</option>
          <option value="tinh">Gom: Tỉnh</option>
        </select>
      </div>

      {/* Bảng — bấm tiêu đề cột = sắp xếp; cột Khách đứng yên khi kéo ngang; hàng tiêu đề nền xám đậm cho rõ (anh Bình 27/10) */}
      <div className="px-3 pt-2">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-x-auto">
          {loading ? <div className="py-8 text-center text-gray-400 text-xs">Đang tải…</div>
          : error ? <div className="py-8 text-center text-red-500 text-xs">{error}</div>
          : hienThi.length === 0 ? <div className="py-8 text-center text-gray-400 text-sm">{rows.length ? 'Không có khách khớp bộ lọc.' : 'Bạn chưa có đơn lẻ nào.'}</div>
          : (
            <table className="text-[12px] border-collapse" style={{ minWidth: COT.reduce((a, c) => a + c.rong, 0) }}>
              <thead>
                <tr className="bg-gray-200">
                  {COT.map((c) => (
                    <th key={c.key} onClick={() => c.key !== 'thao_tac' && bamCot(c.key)} style={{ width: c.rong, minWidth: c.rong }}
                        className={`text-left px-1.5 py-2 font-black text-gray-900 border-b-2 border-gray-500 border-r border-gray-300 cursor-pointer select-none leading-tight ${c.dinh ? 'sticky left-0 bg-gray-200 z-[1]' : ''} ${c.so ? 'text-right' : ''}`}>
                      {c.ten}{sort.key === c.key && <Icon name={sort.dir === 1 ? 'arrow_upward' : 'arrow_downward'} size={12} className="ml-0.5 text-fuchsia-700" />}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cacNhom.map((g) => (
                  <React.Fragment key={g.khoa}>
                    {nhom && (
                      <tr>
                        <td colSpan={COT.length} className="p-0 border-b border-[#cbd5e1]">
                          <button type="button" onClick={() => batNhom(g.khoa)} className="sticky left-0 flex items-center gap-1.5 px-2 py-1.5 text-[12px] font-black" style={{ color: g.mau, background: `${g.mau}14`, width: 'calc(100vw - 24px)', maxWidth: '100%' }}>
                            <Icon name={dong.has(g.khoa) ? 'chevron_right' : 'expand_more'} size={18} />
                            {nhom === 'tinh_trang' && <Icon name={tt[g.khoa]?.icon || 'more_horiz'} size={15} />}
                            <span>{g.nhan}</span>
                            <span className="text-[11px] font-bold rounded-full px-1.5 text-white" style={{ background: g.mau }}>{g.ds.length}</span>
                            <span className="text-[10.5px] font-semibold text-gray-500 ml-1">{fmtTien(g.ds.reduce((a, r) => a + Number(r.tong_tien || 0), 0))} đ</span>
                          </button>
                        </td>
                      </tr>
                    )}
                    {!dong.has(g.khoa) && g.ds.map((r, i) => veDong(r, i))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <p className="text-[10.5px] text-gray-400 mt-1.5 px-1">Bấm ô Tình trạng / Tỉnh / Ghi chú để sửa ngay. Bấm Sửa (hoặc tên khách) mở tấm chi tiết — link có dạng /khach-le?khach=mã. Bấm tiêu đề cột để sắp xếp.</p>
      </div>

      {nhanh && (
        <SuaNhanh r={nhanh.r} cot={nhanh.cot} tt={tt} cities={cities} onClose={() => setNhanh(null)}
                  onLuu={async (data) => { const kq = await luuNhanh(nhanh.r, data); if (kq.success) setNhanh(null); return kq; }} />
      )}
      {moZalo && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center sm:justify-center" onClick={() => setMoZalo(false)}>
          <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-4" onClick={(e) => e.stopPropagation()}>
            <ZaloTroLy zt={zt} />
            <button type="button" onClick={() => setMoZalo(false)} className="mt-3 w-full py-2 rounded-xl bg-gray-100 text-[13px] font-bold text-gray-700">Đóng</button>
          </div>
        </div>
      )}
      {chon && (
        <ChiTietKhach khach={chon} accessToken={accessToken} tt={tt} zt={zt} goiY={dm.goiY} cities={cities} onClose={dongChiTiet} onTaoDon={() => taoDonCho(navigate, chon.id)}
                      onDoiThongTin={(d) => thayDong({ id: chon.id, ...d })}
                      onGop={() => { dongChiTiet(); load(); }}
                      onSaved={thayDong}
                      onViecChanged={() => KhachLeAPI.getList(accessToken).then((x) => x.success && Array.isArray(x.data) && setRows(x.data))} />
      )}
      {taoFb && (
        <FeedbackForm accessToken={accessToken} nguoiSan={{ id: taoFb.id, ten: taoFb.facebook || taoFb.name, code: taoFb.code, loai: 'khach_le' }}
                      onClose={() => setTaoFb(null)}
                      onSaved={(id) => { setTaoFb(null); navigate(`/feedback-ca-nhan/${id}`, { state: { ve: '/khach-le' } }); }} />
      )}
    </div>
  );
};

// ===== Ô Tỉnh thành + địa chỉ chi tiết (dùng ở tấm sửa nhanh + tấm chi tiết). Mặc định chỉ hỏi tỉnh; nút nhỏ mới mở ô địa chỉ (anh Bình 27/10) =====
const OTinh = ({ tinh, diaChi, cities, onChange }) => {
  const [moDiaChi, setMoDiaChi] = useState(!!diaChi);
  // tỉnh cũ ghi "Tỉnh Bình Dương" không khớp bảng city → vẫn hiện làm 1 lựa chọn để không mất
  const ds = cities.some((c) => c.name === tinh) || !tinh ? cities : [{ id: 'cu', name: tinh }, ...cities];
  return (
    <div className="space-y-1.5">
      <select value={tinh || ''} onChange={(e) => onChange({ tinh: e.target.value, diaChi })} className="w-full text-[15px] bg-white border border-gray-300 rounded-lg px-2 py-2 font-medium">
        <option value="">— Chọn tỉnh thành —</option>
        {ds.map((c) => <option key={c.id || c.name} value={c.name}>{c.name}</option>)}
      </select>
      {moDiaChi
        ? <input value={diaChi || ''} onChange={(e) => onChange({ tinh, diaChi: e.target.value })} placeholder="Số nhà, đường, phường, quận… (để gửi hàng)" maxLength={300} autoFocus={!diaChi}
                 className="w-full text-[15px] border border-gray-300 rounded-lg px-2 py-2" />
        : <button type="button" onClick={() => setMoDiaChi(true)} className="text-[11.5px] font-bold text-blue-700 underline underline-offset-2">+ Thêm địa chỉ chi tiết (để nhớ chỗ gửi hàng)</button>}
    </div>
  );
};

// ===== Tấm sửa nhanh từ grid: tình trạng (bấm = lưu ngay) · tỉnh + địa chỉ · ghi chú — không phải vào form lớn (anh Bình 27/10) =====
const SuaNhanh = ({ r, cot, tt, cities, onClose, onLuu }) => {
  const [gia, setGia] = useState({ tinh: r.tinh || '', diaChi: r.dia_chi || '', ghiChu: r.ghi_chu || '' });
  const [dang, setDang] = useState(false);
  const [loi, setLoi] = useState('');
  const luu = async (data) => {
    setDang(true); setLoi('');
    const kq = await onLuu(data);
    setDang(false);
    if (!kq.success) setLoi(kq.error || 'Lưu thất bại');
  };
  const tieuDe = { tinh_trang: 'Tình trạng', tinh: 'Tỉnh thành · địa chỉ', ghi_chu: 'Ghi chú' }[cot];
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center sm:justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-3">
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500">{tieuDe}</p>
            <p className="text-[15px] font-black text-gray-900 truncate">{r.facebook || r.name || '—'}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Đóng" className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"><Icon name="close" size={18} /></button>
        </div>
        {loi && <p className="mb-2 text-[12.5px] text-red-600 bg-red-50 rounded-lg px-2 py-1.5">{loi}</p>}
        {cot === 'tinh_trang' && (
          <div className="flex flex-wrap gap-2">
            {Object.keys(tt).map((k) => (
              <ChipTinhTrang key={k} ma={k} ds={tt} size="lg" noi={r.tinh_trang === k} onClick={() => luu({ tinhTrang: k })} />
            ))}
          </div>
        )}
        {cot === 'tinh' && (
          <>
            <OTinh tinh={gia.tinh} diaChi={gia.diaChi} cities={cities} onChange={(v) => setGia({ ...gia, ...v })} />
            <button type="button" disabled={dang} onClick={() => luu({ tinh: gia.tinh, diaChi: gia.diaChi })} className="mt-3 w-full h-11 rounded-xl bg-[#1E5A35] text-white text-[14px] font-black disabled:opacity-40">Lưu</button>
          </>
        )}
        {cot === 'ghi_chu' && (
          <>
            <textarea value={gia.ghiChu} onChange={(e) => setGia({ ...gia, ghiChu: e.target.value })} rows={3} autoFocus placeholder="Ví dụ: da khô, thích kem 3X, hay mua cuối tháng…"
                      className="w-full text-[15px] border border-gray-300 rounded-xl px-3 py-2 outline-none focus:border-fuchsia-400" />
            <button type="button" disabled={dang} onClick={() => luu({ ghiChu: gia.ghiChu })} className="mt-2 w-full h-11 rounded-xl bg-[#1E5A35] text-white text-[14px] font-black disabled:opacity-40">Lưu ghi chú</button>
          </>
        )}
      </div>
    </div>
  );
};

// ===== Tấm chi tiết 1 khách — 5 mục (anh Bình 27/10/2026): ① Tình trạng (gọn) → ② Đã mua gì → ③ Việc tiếp theo (+ nhắc Zalo) → ④ Việc đã làm (chỉ xoá) → ⑤ Ghi chú
// Việc ③④ cùng 1 bảng khach_le_viec: ③ xong_luc NULL, ④ có xong_luc. Tới hạn thì tự hiện trong /bao-cao-ngay (tick bên đó = chuyển sang ④).
// ⑥ Hình ảnh ghi chú đã bỏ: ảnh cũ nằm trong hồ sơ Kết quả khách (/feedback-ca-nhan/<id>, mốc Trong).

const ngayISO = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const congNgay = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return ngayISO(d); };
const soNgayTruoc = (iso) => Math.round((new Date(ngayISO()) - new Date(ngayISO(new Date(iso)))) / 86400000);
const ddmm = (iso) => (iso ? `${String(iso).slice(8, 10)}/${String(iso).slice(5, 7)}` : '');
const NHAC_SAU = [[0, 'Hôm nay'], [1, '1 ngày'], [3, '3 ngày'], [7, '7 ngày'], [14, '14 ngày'], [30, '30 ngày']];
const NAM = new Date().getFullYear();

const Muc = ({ so, ten, phu, children }) => (
  <section className="pt-4">
    <div className="flex items-center gap-2 mb-2">
      <span className="w-7 h-7 rounded-full bg-[#1E5A35] text-white text-[14px] font-black flex items-center justify-center shrink-0 shadow">{so}</span>
      <h3 className="text-[15px] font-black text-gray-900">{ten}</h3>
      {phu && <span className="ml-auto text-[11.5px] font-semibold text-gray-500 text-right">{phu}</span>}
    </div>
    <div className="pl-9">{children}</div>
  </section>
);

// Chọn ngày hẹn: chip "sau N ngày" HOẶC 3 ô Ngày/Tháng/Năm tiếng Việt (DateSelect — <input type=date> hiện tiếng Anh, anh Bình chê 27/10)
const ChonHan = ({ han, onChange }) => {
  const [tuChon, setTuChon] = useState(() => !!han && !NHAC_SAU.some(([n]) => congNgay(n) === han));
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1.5 items-center">
        <span className="text-[11.5px] font-bold text-gray-600">Sau:</span>
        {NHAC_SAU.map(([n, t]) => (
          <button key={n} type="button" onClick={() => { setTuChon(false); onChange(congNgay(n)); }}
                  className={`px-2.5 py-1 rounded-full text-[12px] font-bold border-2 ${!tuChon && han === congNgay(n) ? 'bg-[#1E5A35] text-white border-[#1E5A35]' : 'bg-white text-gray-700 border-gray-300'}`}>{t}</button>
        ))}
        <button type="button" onClick={() => setTuChon(true)} className={`px-2.5 py-1 rounded-full text-[12px] font-bold border-2 ${tuChon ? 'bg-[#1E5A35] text-white border-[#1E5A35]' : 'bg-white text-gray-700 border-gray-300'}`}>📅 Chọn ngày</button>
      </div>
      {tuChon && <DateSelect value={han || ''} onChange={onChange} yearFrom={NAM} yearTo={NAM + 1} />}
    </div>
  );
};

const ChiTietKhach = ({ khach, accessToken, tt, zt, goiY, cities, onClose, onSaved, onTaoDon, onDoiThongTin, onGop, onViecChanged }) => {
  const [ghiChu, setGhiChu] = useState(khach.ghi_chu || '');
  const [sua, setSua] = useState(null); // null = không sửa; { ten, sdt } = đang mở ô Sửa thông tin
  const [suaDc, setSuaDc] = useState(null); // { tinh, diaChi } đang sửa địa chỉ
  const [trung, setTrung] = useState(null); // SĐT mới đã là khách lẻ khác → hỏi gộp
  const [dangLuu, setDangLuu] = useState(false);
  const [loi, setLoi] = useState('');
  const [don, setDon] = useState(null);
  const [xemHetDon, setXemHetDon] = useState(false);
  const [viec, setViec] = useState([]);
  const [chonViec, setChonViec] = useState(null); // 'da_lam' | 'can_lam' — đang mở bảng gợi ý
  const [tuGoDaLam, setTuGoDaLam] = useState('');
  const [moi, setMoi] = useState({ ten: '', han: congNgay(3), zalo: true }); // thêm nhanh việc tiếp theo
  const [suaHanId, setSuaHanId] = useState(null); // việc đang mở ô đổi ngày hẹn
  const [doiTT, setDoiTT] = useState(false); // ① đang mở hàng chọn tình trạng
  const ttHienTai = tt[khach.tinh_trang] || tt.khac || {};

  // Sửa thông tin = tên + SĐT 1 lần (anh Bình 22/10/2026). SĐT chỉ gửi khi đổi → đại lý gắn thêm (không được sửa SĐT) vẫn sửa được tên.
  const luuThongTin = async (gop = false) => {
    setDangLuu(true); setLoi('');
    const doiSdt = sua.sdt !== khach.sdt;
    const r = await KhachLeAPI.suaThongTin(accessToken, khach.id, { ten: sua.ten.trim(), sdt: doiSdt ? sua.sdt : undefined, gop });
    setDangLuu(false);
    if (!r.success) setLoi(r.error || 'Sửa thông tin thất bại');
    else if (r.data?.canGop) setTrung(r.data.khachTrung);
    else if (r.data?.gop) onGop();
    else { onDoiThongTin({ sdt: sua.sdt, facebook: sua.ten.trim() || khach.facebook }); setSua(null); }
  };

  const loadViec = useCallback(async () => {
    const r = await KhachLeAPI.getViec(accessToken, khach.id);
    if (r.success) setViec(Array.isArray(r.data) ? r.data : []);
  }, [accessToken, khach.id]);
  useEffect(() => {
    loadViec();
    KhachLeAPI.getDon(accessToken, khach.id).then((r) => setDon(r.success && Array.isArray(r.data) ? r.data : []));
  }, [loadViec, accessToken, khach.id]);

  const luu = async (data) => {
    setDangLuu(true); setLoi('');
    const r = await KhachLeAPI.capNhat(accessToken, khach.id, data);
    setDangLuu(false);
    if (r.success && r.data) { onSaved(r.data); return true; }
    setLoi(r.error || 'Lưu thất bại'); return false;
  };

  const themViec = async (data) => {
    setLoi('');
    const r = await KhachLeAPI.themViec(accessToken, khach.id, data);
    if (r.success) { loadViec(); setChonViec(null); onViecChanged(); } else setLoi(r.error || 'Thêm việc thất bại');
    return r.success;
  };
  const xoaViec = async (v) => {
    if (!window.confirm(`Xoá việc "${v.ten}"?`)) return;
    await KhachLeAPI.xoaViec(accessToken, v.id); loadViec(); onViecChanged();
  };
  const doiHan = async (v, han) => {
    if (!han) return;
    setViec((ds) => ds.map((x) => (x.id === v.id ? { ...x, han, da_nhac_luc: null } : x)));
    const r = await KhachLeAPI.suaViec(accessToken, v.id, { han });
    if (!r.success) setLoi(r.error || 'Lưu ngày hẹn thất bại');
    loadViec(); onViecChanged();
  };

  const daLam = viec.filter((v) => v.xong_luc);
  const canLam = viec.filter((v) => !v.xong_luc);
  const donHien = don ? (xemHetDon ? don : don.slice(0, 3)) : [];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center sm:justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md max-h-[92vh] rounded-t-3xl sm:rounded-3xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-white px-4 pt-3 pb-2 border-b border-gray-100 flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-[16px] font-black text-gray-900 truncate">{khach.facebook || khach.name || '—'}</p>
            <p className="text-[11.5px] text-gray-500 flex items-center gap-2">{sdtNgan(khach.sdt)} <NutGoi sdt={khach.sdt} nhan="Gọi điện" /> <NutZalo sdt={khach.sdt} nhan="Nhắn Zalo" />
              {sua === null && <button type="button" onClick={() => setSua({ ten: khach.facebook || '', sdt: khach.sdt || '' })} className="text-[11px] font-bold text-blue-700 underline whitespace-nowrap">Sửa thông tin</button>}</p>
            {sua !== null && (
              <div className="mt-1.5 space-y-1.5">
                <input type="text" value={sua.ten} autoFocus placeholder="Tên khách" maxLength={80}
                       onChange={(e) => setSua({ ...sua, ten: e.target.value })}
                       className="w-full text-[16px] border border-gray-300 rounded-lg px-2 py-1.5" />
                <div className="flex gap-1.5">
                  <input type="tel" inputMode="numeric" value={sua.sdt} placeholder="SĐT (10 số)"
                         onChange={(e) => { setSua({ ...sua, sdt: e.target.value.replace(/\D/g, '').slice(0, 10) }); setTrung(null); }}
                         className="flex-1 min-w-0 text-[16px] border border-gray-300 rounded-lg px-2 py-1.5" />
                  <button type="button" onClick={() => luuThongTin()} disabled={dangLuu || sua.sdt.length !== 10 || !sua.ten.trim() || !!trung}
                          className="px-3 rounded-lg bg-[#1E5A35] text-white text-[12px] font-bold disabled:opacity-40">Lưu</button>
                  <button type="button" onClick={() => { setSua(null); setLoi(''); setTrung(null); }} className="px-2 rounded-lg bg-gray-100 text-[12px] font-bold text-gray-600">Huỷ</button>
                </div>
              </div>
            )}
            {sua !== null && trung && (
              <div className="mt-1.5 rounded-xl border border-amber-300 bg-amber-50 p-2.5 text-[12.5px] text-amber-900">
                <p>SĐT này đã là khách <b>{trung.code} - {trung.facebook || '—'}</b>
                  {trung.daiLy ? <> — đang là khách lẻ của <b>{trung.daiLy}</b>.</> : ' trong danh sách của bạn.'}</p>
                <p className="mt-1 text-amber-800">Gộp lại: đơn hàng, ghi chú, hồ sơ kết quả của khách này chuyển sang khách đúng SĐT; tên bạn đặt vẫn giữ.</p>
                <div className="flex gap-1.5 mt-2">
                  <button type="button" onClick={() => luuThongTin(true)} disabled={dangLuu}
                          className="flex-1 h-10 rounded-lg bg-amber-600 text-white text-[13px] font-bold disabled:opacity-40">Gộp vào khách này</button>
                  <button type="button" onClick={() => setTrung(null)} className="px-3 h-10 rounded-lg bg-white border border-amber-300 text-[13px] font-bold text-amber-800">Không</button>
                </div>
              </div>
            )}
            {/* Địa chỉ: tỉnh (bắt buộc hỏi) + địa chỉ chi tiết (nút nhỏ mới mở) — cùng bảng city với /cap-nhat-thong-tin */}
            {suaDc === null ? (
              <p className="text-[11.5px] text-gray-600 mt-0.5 flex items-start gap-1">
                <Icon name="location_on" size={14} className="text-[#1E5A35] shrink-0 mt-px" />
                <span className="min-w-0 truncate">{khach.tinh ? <b className="text-gray-900">{rutTinh(khach.tinh)}</b> : <span className="text-gray-400">Chưa có tỉnh thành</span>}{khach.dia_chi ? ` · ${khach.dia_chi}` : ''}</span>
                <button type="button" onClick={() => setSuaDc({ tinh: khach.tinh || '', diaChi: khach.dia_chi || '' })} className="shrink-0 text-[11px] font-bold text-blue-700 underline">Sửa</button>
              </p>
            ) : (
              <div className="mt-1.5 space-y-1.5">
                <OTinh tinh={suaDc.tinh} diaChi={suaDc.diaChi} cities={cities} onChange={(v) => setSuaDc({ ...suaDc, ...v })} />
                <div className="flex gap-1.5">
                  <button type="button" disabled={dangLuu} onClick={async () => { if (await luu({ tinh: suaDc.tinh, diaChi: suaDc.diaChi })) setSuaDc(null); }}
                          className="flex-1 h-9 rounded-lg bg-[#1E5A35] text-white text-[12px] font-bold disabled:opacity-40">Lưu địa chỉ</button>
                  <button type="button" onClick={() => setSuaDc(null)} className="px-3 h-9 rounded-lg bg-gray-100 text-[12px] font-bold text-gray-600">Huỷ</button>
                </div>
              </div>
            )}
          </div>
          <button type="button" onClick={onTaoDon} className="px-3 h-8 rounded-full bg-[#1E5A35] text-white text-[12px] font-bold whitespace-nowrap">+ Tạo đơn</button>
          <button type="button" onClick={onClose} aria-label="Đóng" className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"><Icon name="close" size={18} /></button>
        </div>

        <div className="px-4 pb-6">
          {loi && <p className="mt-2 text-[12.5px] text-red-600 bg-red-50 rounded-lg px-2 py-1.5">{loi}</p>}

          {/* ① gọn 1 dòng: tình trạng hiện tại nổi bật (nền đậm) + nút Đổi → mới xổ các lựa chọn khác. Bỏ "Bước tiếp" (anh Bình 27/10) */}
          <Muc so="1" ten="Tình trạng" phu={!khach.tinh_trang_tay ? 'đang gợi ý theo ngày mua' : null}>
            <div className="flex items-center gap-2">
              <ChipTinhTrang ma={khach.tinh_trang} ds={tt} size="lg" noi onClick={() => setDoiTT(!doiTT)} />
              <button type="button" onClick={() => setDoiTT(!doiTT)} className="text-[12px] font-bold text-blue-700 underline underline-offset-2">{doiTT ? 'Đóng' : 'Đổi'}</button>
            </div>
            {doiTT && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {Object.keys(tt).filter((k) => k !== khach.tinh_trang).map((k) => (
                  <ChipTinhTrang key={k} ma={k} ds={tt} size="lg" onClick={async () => { if (await luu({ tinhTrang: k })) setDoiTT(false); }} />
                ))}
              </div>
            )}
          </Muc>

          <Muc so="2" ten="Đã mua gì" phu={`${khach.so_lan_mua} lần · ${fmtTien(khach.tong_tien)} đ`}>
            {don === null ? <p className="text-xs text-gray-400">Đang tải…</p>
            : don.length === 0 ? <p className="text-xs text-gray-400">Chưa có đơn.</p>
            : (
              <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl">
                {donHien.map((d) => {
                  const n = soNgayTruoc(d.created_at);
                  return (
                    <div key={d.id} className="flex gap-2 px-2.5 py-2">
                      <div className="w-[68px] shrink-0">
                        <p className={`text-[12.5px] font-black ${n > 60 ? 'text-red-600' : n > 30 ? 'text-amber-600' : 'text-green-700'}`}>{cachDay(n)}{n > 1 ? ' trước' : ''}</p>
                        <p className="text-[10.5px] text-gray-400">{ddmm(d.created_at)}</p>
                      </div>
                      <p className="flex-1 text-[12px] text-gray-700 leading-snug">{(d.sp || []).map((s) => `${s.ten} ×${s.sl}`).join(', ') || '—'}</p>
                      <p className="text-[12px] font-bold text-gray-900 whitespace-nowrap">{fmtTien(d.total_amount)}</p>
                    </div>
                  );
                })}
                {don.length > 3 && (
                  <button type="button" onClick={() => setXemHetDon(!xemHetDon)} className="w-full py-1.5 text-[11.5px] font-bold text-blue-700">
                    {xemHetDon ? 'Thu gọn' : `Xem thêm ${don.length - 3} đơn`}
                  </button>
                )}
              </div>
            )}
          </Muc>

          <Muc so="3" ten="Việc tiếp theo" phu={canLam.length ? `${canLam.length} việc` : null}>
            {canLam.map((v, i) => {
              const quaHan = v.han && v.han.slice(0, 10) < ngayISO();
              return (
                <div key={v.id} className="py-1.5 px-2 rounded-lg bg-gray-50 mb-1">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-[#1E5A35] text-white text-[10px] font-black flex items-center justify-center shrink-0">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold text-gray-900">{v.ten}</p>
                      {/* Bấm ngày để sửa — đổi ngày thì Zalo nhắc lại từ đầu (BE xoá da_nhac_luc) */}
                      <button type="button" onClick={() => setSuaHanId(suaHanId === v.id ? null : v.id)} className={`text-[11px] ${quaHan ? 'text-red-600 font-bold' : 'text-gray-600'}`}>
                        ⏰ {v.han ? fmtNgay(v.han) : 'chưa hẹn ngày'}{quaHan ? ' · quá hạn' : ''}{v.han && v.nhac_zalo ? ` · 📲${v.da_nhac_luc ? (v.loi_nhac ? ' lỗi' : ' đã nhắc') : ' 7:30'}` : ''} <span className="underline">sửa</span>
                      </button>
                    </div>
                    <button type="button" onClick={() => xoaViec(v)} aria-label="Xoá việc" className="text-gray-400"><Icon name="delete" size={18} /></button>
                  </div>
                  {suaHanId === v.id && <div className="mt-1.5 pl-7"><ChonHan han={v.han ? v.han.slice(0, 10) : ''} onChange={(h) => { doiHan(v, h); setSuaHanId(null); }} /></div>}
                </div>
              );
            })}
            {/* Thêm nhanh: gõ tên → chọn "sau N ngày" hoặc ngày (tiếng Việt) → Lưu. Gợi ý thông minh vẫn ở nút bên cạnh */}
            <div className="mt-1 rounded-xl border-2 border-dashed border-[#1E5A35]/40 p-2 space-y-1.5">
              <div className="flex gap-1.5">
                <input value={moi.ten} onChange={(e) => setMoi({ ...moi, ten: e.target.value })} placeholder="Việc tiếp theo với khách…"
                       className="flex-1 min-w-0 text-[14px] border border-gray-300 rounded-lg px-2 py-1.5" />
                <button type="button" onClick={() => setChonViec('can_lam')} className="px-3 rounded-lg bg-[#F0F7F2] border border-[#1E5A35]/40 text-[#1E5A35] text-[12px] font-bold whitespace-nowrap">Gợi ý</button>
              </div>
              {moi.ten.trim() && (
                <>
                  <ChonHan han={moi.han} onChange={(h) => setMoi({ ...moi, han: h })} />
                  <label className="flex items-center gap-2 text-[12px] text-gray-700">
                    <input type="checkbox" checked={moi.zalo} onChange={(e) => setMoi({ ...moi, zalo: e.target.checked })} className="w-4 h-4" />
                    📲 Zalo nhắc tôi 7:30 sáng {ddmm(moi.han)} + thêm vào Báo cáo ngày hôm đó
                  </label>
                  <button type="button" disabled={!moi.han} onClick={async () => { if (await themViec({ ten: moi.ten, han: moi.han, nhacZalo: moi.zalo })) setMoi({ ten: '', han: congNgay(3), zalo: true }); }}
                          className="w-full h-10 rounded-lg bg-[#1E5A35] text-white text-[13px] font-black disabled:opacity-40">Lưu việc tiếp theo</button>
                </>
              )}
            </div>
            <div className="mt-1.5"><ZaloTroLy zt={zt} gon /></div>
          </Muc>

          <Muc so="4" ten="Việc đã làm" phu={daLam.length ? `${daLam.length} việc` : null}>
            {daLam.map((v, i) => (
              <div key={v.id} className="flex items-center gap-2 py-1.5 px-2 rounded-lg bg-green-50 mb-1">
                <span className="w-5 h-5 rounded-full bg-green-600 text-white text-[10px] font-black flex items-center justify-center shrink-0">{i + 1}</span>
                <span className="flex-1 text-[13px] text-green-800 font-medium">{v.ten}</span>
                <span className="text-[10.5px] text-gray-400">{ddmm(ngayISO(new Date(v.xong_luc)))}</span>
                <button type="button" onClick={() => xoaViec(v)} aria-label="Xoá việc" className="text-gray-400"><Icon name="delete" size={18} /></button>
              </div>
            ))}
            <div className="flex gap-1.5 mt-1">
              <input value={tuGoDaLam} onChange={(e) => setTuGoDaLam(e.target.value)} placeholder="Đã làm gì với khách…"
                     onKeyDown={(e) => { if (e.key === 'Enter' && tuGoDaLam.trim()) { themViec({ ten: tuGoDaLam, xong: true }); setTuGoDaLam(''); } }}
                     className="flex-1 min-w-0 text-[14px] border border-gray-300 rounded-lg px-2 py-1.5" />
              {tuGoDaLam.trim()
                ? <button type="button" onClick={() => { themViec({ ten: tuGoDaLam, xong: true }); setTuGoDaLam(''); }} className="px-3 rounded-lg bg-green-600 text-white text-[12px] font-bold">Thêm</button>
                : <button type="button" onClick={() => setChonViec('da_lam')} className="px-3 rounded-lg bg-green-50 border border-green-300 text-green-800 text-[12px] font-bold whitespace-nowrap">Gợi ý</button>}
            </div>
          </Muc>

          <Muc so="5" ten="Ghi chú về khách">
            <textarea value={ghiChu} onChange={(e) => setGhiChu(e.target.value)} rows={2} placeholder="Ví dụ: da khô, thích kem 3X, hay mua cuối tháng…"
                      className="w-full text-[14px] border border-gray-200 rounded-xl px-3 py-2 outline-none focus:border-fuchsia-400" />
            {ghiChu !== (khach.ghi_chu || '') && (
              <div className="flex justify-end mt-1">
                <button type="button" disabled={dangLuu} onClick={() => luu({ ghiChu })}
                        className="px-3 py-1.5 rounded-lg bg-[#1E5A35] text-white text-[12px] font-bold disabled:opacity-40">Lưu ghi chú</button>
              </div>
            )}
          </Muc>
        </div>
      </div>

      {chonViec && (
        <BangGoiYViec loai={chonViec} goiY={goiY} tinhTrang={khach.tinh_trang} ttTen={ttHienTai.ten} daCo={canLam.map((v) => v.goi_y_id)}
                      onClose={() => setChonViec(null)} onChon={themViec} />
      )}
    </div>
  );
};

// Bảng gợi ý việc (tấm trượt thứ 2). Hiện TÊN NGẮN + chi tiết; lưu xong thẻ chỉ còn tên ngắn.
// can_lam: gợi ý hợp tình trạng hiện tại lên đầu → chọn → nhắc sau N ngày (+ Zalo 7:30 sáng ngày đó). da_lam: chọn là ghi vào "Việc đã làm" luôn.
const BangGoiYViec = ({ loai, goiY, tinhTrang, ttTen, daCo, onClose, onChon }) => {
  const [chon, setChon] = useState(null); // { ten, goiYId, han }
  const [tuGo, setTuGo] = useState('');
  const [zalo, setZalo] = useState(true);
  const hop = goiY.filter((g) => g.tinh_trang === tinhTrang && !daCo.includes(g.id));
  const khac = goiY.filter((g) => g.tinh_trang !== tinhTrang && !daCo.includes(g.id));
  const bam = (g) => {
    if (loai === 'da_lam') return onChon({ ten: g.ten_ngan, goiYId: g.id, xong: true });
    setChon({ ten: g.ten_ngan, goiYId: g.id, han: congNgay(g.nhac_sau_ngay ?? 3) });
  };
  // Thẻ gợi ý: chưa chọn = trắng (nhóm "Nên làm" viền vàng), ĐANG CHỌN = nền xanh đậm chữ trắng + ✓ (anh Bình: phải nhìn ra ngay mình đã chọn gì)
  const The = ({ g, nen }) => {
    const dangChon = chon?.goiYId === g.id;
    return (
      <button type="button" onClick={() => bam(g)}
              className={`w-full text-left rounded-2xl border-2 px-3 py-3 mb-2 flex items-start gap-3 transition-all ${dangChon ? 'bg-[#1E5A35] border-[#1E5A35] shadow-lg scale-[1.02]' : nen ? 'bg-amber-50 border-amber-300' : 'bg-white border-gray-200'}`}>
        <span className={`mt-0.5 w-6 h-6 rounded-full border-2 shrink-0 flex items-center justify-center ${dangChon ? 'bg-white border-white' : 'border-gray-400 bg-white'}`}>
          {dangChon && <Icon name="check" size={18} className="text-[#1E5A35]" />}
        </span>
        <span className="flex-1 min-w-0">
          <span className={`block text-[16px] font-black leading-tight ${dangChon ? 'text-white' : 'text-gray-900'}`}>{g.ten_ngan}</span>
          {g.chi_tiet && <span className={`block text-[12.5px] leading-snug mt-0.5 ${dangChon ? 'text-white/85' : 'text-gray-500'}`}>{g.chi_tiet}</span>}
        </span>
      </button>
    );
  };
  const buoc = loai === 'can_lam' ? (chon ? 2 : 1) : 1;
  const Buoc = ({ so, ten }) => (
    <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-[11.5px] font-black ${buoc === so ? 'bg-[#1E5A35] text-white' : buoc > so ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-500'}`}>
      <span>{buoc > so ? '✓' : so}</span>{ten}
    </span>
  );
  return (
    <div className="fixed inset-0 z-[55] bg-black/40 flex items-end sm:items-center sm:justify-center" onClick={onClose}>
      <div className="bg-gray-50 w-full sm:max-w-md max-h-[88vh] rounded-t-3xl sm:rounded-3xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-gray-50 px-4 pt-3 pb-2 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <p className="flex-1 text-[16px] font-black text-gray-900">{loai === 'da_lam' ? '④ Việc đã làm với khách?' : '③ Việc tiếp theo với khách?'}</p>
            <button type="button" onClick={onClose} aria-label="Đóng" className="w-8 h-8 rounded-full bg-white flex items-center justify-center"><Icon name="close" size={18} /></button>
          </div>
          {loai === 'can_lam' && (
            <div className="flex items-center gap-1 mt-1.5"><Buoc so={1} ten="Chọn việc" /><span className="text-gray-400">→</span><Buoc so={2} ten="Hẹn ngày nhắc" /></div>
          )}
        </div>
        <div className="px-4 py-3">
          <div className="flex gap-1.5 mb-3">
            <input value={tuGo} onChange={(e) => { setTuGo(e.target.value); if (chon && !chon.goiYId) setChon({ ...chon, ten: e.target.value }); }}
                   placeholder="Tự gõ việc khác…" className="flex-1 min-w-0 text-[15px] border border-gray-300 rounded-lg px-2 py-2 bg-white" />
            <button type="button" disabled={!tuGo.trim()}
                    onClick={() => (loai === 'da_lam' ? onChon({ ten: tuGo, xong: true }) : setChon({ ten: tuGo.trim(), goiYId: null, han: chon?.han ?? congNgay(3) }))}
                    className="px-3 rounded-lg bg-[#1E5A35] text-white text-[13px] font-bold disabled:opacity-40">Chọn</button>
          </div>
          {hop.length > 0 && <p className="text-[12px] font-black text-amber-700 mb-1.5">⭐ Nên làm — hợp tình trạng "{ttTen}"</p>}
          {hop.map((g) => <The key={g.id} g={g} nen />)}
          {khac.length > 0 && <p className="text-[12px] font-bold text-gray-500 mt-3 mb-1.5">Việc khác</p>}
          {khac.map((g) => <The key={g.id} g={g} />)}
        </div>
        {loai === 'can_lam' && !chon && (
          <div className="sticky bottom-0 bg-amber-100 border-t-2 border-amber-300 px-4 py-3 text-center text-[14px] font-black text-amber-900">
            👆 Bước 1: bấm chọn 1 việc ở trên
          </div>
        )}
        {loai === 'can_lam' && chon && (
          <div className="sticky bottom-0 bg-white border-t-2 border-[#1E5A35] px-4 py-3 space-y-2 shadow-[0_-8px_20px_rgba(0,0,0,0.08)]">
            <p className="flex items-center gap-1.5 text-[14px] font-black text-[#1E5A35]"><Icon name="check_circle" size={18} />{chon.ten}</p>
            <p className="text-[12.5px] font-bold text-gray-700">Bước 2: hẹn ngày</p>
            <ChonHan han={chon.han} onChange={(h) => setChon({ ...chon, han: h })} />
            <label className="flex items-center gap-2 text-[12.5px] text-gray-700">
              {chon.han > ngayISO() && <input type="checkbox" checked={zalo} onChange={(e) => setZalo(e.target.checked)} className="w-4 h-4" />}
              {chon.han > ngayISO() ? `📲 Zalo nhắc tôi 7:30 sáng ${ddmm(chon.han)} + thêm vào Báo cáo ngày hôm đó` : '📋 Thêm vào Báo cáo ngày hôm nay'}
            </label>
            <button type="button" disabled={!chon.han} onClick={() => onChon({ ten: chon.ten, goiYId: chon.goiYId, han: chon.han, nhacZalo: chon.han > ngayISO() && zalo })}
                    className="w-full h-12 rounded-xl bg-[#1E5A35] text-white text-[15px] font-black disabled:opacity-40">Lưu việc tiếp theo</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default KhachLePage;
